// Data + email helpers for the frontend. Now backed by the self-hosted API
// (via ../api). Admin endpoints automatically include the JWT.
import { apiGet, apiPost, apiDelete } from "../api";

// ---------------------------------------------------------------------------
// Email
// ---------------------------------------------------------------------------
export const sendAppointmentConfirmationEmail = async (userData, appointmentData = null) => {
  try {
    return await apiPost("/sendAppointmentConfirmation", { userData, appointmentData });
  } catch (error) {
    console.error("Error sending confirmation email:", error);
    return { success: false, error: error.message };
  }
};

export const sendAdminNotificationEmail = async (userData, appointmentData = null) => {
  try {
    return await apiPost("/sendAdminNotification", { userData, appointmentData });
  } catch (error) {
    console.error("Error sending admin notification:", error);
    return { success: false, error: error.message };
  }
};

// ---------------------------------------------------------------------------
// Users / appointments
// ---------------------------------------------------------------------------
export const saveUserInfo = async (userData) => {
  const res = await apiPost("/saveUser", userData);
  return res.userId;
};

export const saveAppointment = async (userId, appointmentData) => {
  try {
    return await apiPost("/saveAppointment", { userId, appointmentData });
  } catch (error) {
    if (error.code === "SLOT_ALREADY_BOOKED") {
      throw new Error("Bu tarih ve saat için zaten bir randevu bulunmaktadır. Lütfen başka bir saat seçin.");
    }
    throw error;
  }
};

export const getBookedSlots = async (date) => {
  try {
    const d = date instanceof Date
      ? `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}`
      : date;
    return (await apiGet("/getBookedSlots", { date: d })).bookedSlots || [];
  } catch (error) {
    console.error("Error getting booked slots:", error);
    return [];
  }
};

export const getAllAppointments = async () => {
  try { return (await apiGet("/getAllAppointments")).appointments || []; }
  catch (error) { console.error("Error getting appointments:", error); return []; }
};

export const updateAppointmentStatus = async (appointmentId, status) =>
  apiPost("/updateAppointmentStatus", { appointmentId, status });

export const deleteAppointment = async (appointmentId) =>
  apiDelete("/deleteAppointment", { appointmentId });

export const getAvailabilitySettings = async () =>
  (await apiGet("/getAvailabilitySettings")).settings;

export const saveAvailabilitySettings = async (settings) =>
  apiPost("/saveAvailabilitySettings", settings);

// ---------------------------------------------------------------------------
// Students
// ---------------------------------------------------------------------------
export const getAllStudents = async () => {
  try { return (await apiGet("/getAllStudents")).students || []; }
  catch (error) { console.error("Error getting students:", error); return []; }
};

export const addStudent = async (studentData) =>
  (await apiPost("/addStudent", studentData)).student;

export const updateStudent = async (studentId, studentData) =>
  apiPost("/updateStudent", { studentId, studentData });

export const deleteStudent = async (studentId) =>
  apiDelete("/deleteStudent", { studentId });

// ---------------------------------------------------------------------------
// Validators / formatters (unchanged, pure functions)
// ---------------------------------------------------------------------------
export const validateEmail = (email) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);

export const formatUserDataForEmail = (formData) => ({
  firstName: formData.firstName || "",
  lastName: formData.lastName || "",
  email: formData.email || "",
  phone: formData.phone || "",
  age: formData.age || "",
  education: formData.education || "",
});

export const formatAppointmentDataForEmail = (appointmentInfo) => {
  if (!appointmentInfo) return null;
  let formattedDate = "Belirtilmemiş";
  if (appointmentInfo.date) {
    try {
      let date;
      if (appointmentInfo.date instanceof Date) {
        date = appointmentInfo.date;
      } else if (typeof appointmentInfo.date === "string" && appointmentInfo.date.match(/^\d{4}-\d{2}-\d{2}$/)) {
        const [year, month, day] = appointmentInfo.date.split("-");
        date = new Date(parseInt(year), parseInt(month) - 1, parseInt(day));
      } else {
        date = new Date(appointmentInfo.date);
      }
      formattedDate = date.toLocaleDateString("tr-TR", {
        weekday: "long", year: "numeric", month: "long", day: "numeric",
      });
    } catch (error) {
      console.error("Error formatting date:", error);
      formattedDate = "Geçersiz tarih";
    }
  }
  return {
    date: formattedDate,
    time: appointmentInfo.time || "Belirtilmemiş",
    duration: appointmentInfo.duration || "30 dakika",
    type: appointmentInfo.type || "Genel Danışmanlık",
    meetLink: appointmentInfo.meetLink || null,
  };
};

// ---------------------------------------------------------------------------
// Incomplete appointments
// ---------------------------------------------------------------------------
export const saveIncompleteAppointment = async (userData) =>
  (await apiPost("/saveIncompleteAppointment", userData)).id;

export const getIncompleteAppointments = async () => {
  try { return (await apiGet("/getIncompleteAppointments")).incompleteAppointments || []; }
  catch (error) { console.error("Error getting incomplete appointments:", error); return []; }
};

export const deleteIncompleteAppointment = async (appointmentId) =>
  apiDelete("/deleteIncompleteAppointment", { appointmentId });

export const cleanupIncompleteAppointment = async (email) => {
  try {
    const { cleanupIncompleteAppointment: cleanupService } = await import("../services/appointmentCleanup");
    return await cleanupService(email);
  } catch (error) {
    console.error("Error cleaning up incomplete appointment:", error);
    return { success: false, error: error.message };
  }
};

// ---------------------------------------------------------------------------
// Appointment completion
// ---------------------------------------------------------------------------
export const completeAppointmentWithDetails = async (appointmentId, completionData) =>
  apiPost("/completeAppointmentWithDetails", { appointmentId, completionData });

export const getCompletedAppointments = async () => {
  try { return (await apiGet("/getCompletedAppointments")).appointments || []; }
  catch (error) { console.error("Error getting completed appointments:", error); return []; }
};

// ---------------------------------------------------------------------------
// Performance endpoints
// ---------------------------------------------------------------------------
export const getBatchCalendarData = async (selectedDate = null, dateRange = 30) =>
  apiGet("/getBatchCalendarData", { selectedDate, dateRange });

export const getBookedSlotsForDateRange = async (startDate, endDate, currentMonth = null) =>
  apiGet("/getBookedSlotsForDateRange", { startDate, endDate, currentMonth });

// ---------------------------------------------------------------------------
// Client-side cache (unchanged)
// ---------------------------------------------------------------------------
class CalendarDataCache {
  constructor() {
    this.cache = new Map();
    this.cacheTimestamps = new Map();
    this.defaultTTL = 30 * 1000;
  }
  set(key, data, ttl = this.defaultTTL) {
    this.cache.set(key, data);
    this.cacheTimestamps.set(key, Date.now() + ttl);
  }
  get(key) {
    const timestamp = this.cacheTimestamps.get(key);
    if (!timestamp || Date.now() > timestamp) {
      this.cache.delete(key);
      this.cacheTimestamps.delete(key);
      return null;
    }
    return this.cache.get(key);
  }
  has(key) { return this.get(key) !== null; }
  clear() { this.cache.clear(); this.cacheTimestamps.clear(); }
  cleanup() {
    const now = Date.now();
    for (const [key, timestamp] of this.cacheTimestamps.entries()) {
      if (now > timestamp) { this.cache.delete(key); this.cacheTimestamps.delete(key); }
    }
  }
}

export const calendarCache = new CalendarDataCache();

export const getBatchCalendarDataCached = async (selectedDate = null, dateRange = 30) => {
  const cacheKey = `batch_${selectedDate || "null"}_${dateRange}`;
  const cached = calendarCache.get(cacheKey);
  if (cached) return cached;
  const result = await getBatchCalendarData(selectedDate, dateRange);
  const ttl = result.cache?.ttl ? result.cache.ttl * 1000 : 30 * 1000;
  calendarCache.set(cacheKey, result, ttl);
  return result;
};

export const getBookedSlotsForDateRangeCached = async (startDate, endDate, currentMonth = null) => {
  const cacheKey = `range_${startDate}_${endDate}_${currentMonth || "null"}`;
  const cached = calendarCache.get(cacheKey);
  if (cached) return cached;
  const result = await getBookedSlotsForDateRange(startDate, endDate, currentMonth);
  const ttl = result.cache?.ttl ? result.cache.ttl * 1000 : 30 * 1000;
  calendarCache.set(cacheKey, result, ttl);
  return result;
};

export const performanceMonitor = {
  startTiming(label) { if (typeof performance !== "undefined") performance.mark(`${label}-start`); },
  endTiming(label) {
    if (typeof performance !== "undefined") {
      performance.mark(`${label}-end`);
      try {
        performance.measure(label, `${label}-start`, `${label}-end`);
        const measure = performance.getEntriesByName(label, "measure")[0];
        return measure.duration;
      } catch (e) { /* ignore */ }
    }
    return null;
  },
  logApiCall(functionName, startTime, success = true, error = null) {
    const duration = Date.now() - startTime;
    console.log(`API Call: ${functionName} - ${success ? "SUCCESS" : "ERROR"} - ${duration}ms`, {
      function: functionName, duration, success, error: error?.message,
    });
  },
};

export const clearCalendarCache = () => { calendarCache.clear(); };

export const getCacheStats = () => ({
  cacheEntries: calendarCache.cache.size,
  timestampEntries: calendarCache.cacheTimestamps.size,
  cacheKeys: Array.from(calendarCache.cache.keys()),
});

if (typeof window !== "undefined") {
  setInterval(() => calendarCache.cleanup(), 10 * 60 * 1000);
}

// Re-export query-code helpers from the API client
export {
  findStudentByQueryCode,
  generateQueryCode,
  ensureQueryCode,
} from "../firebase.js";
